import { useEffect, useRef, useState } from "react";
import { Play, Pause, Loader2, VolumeX, Volume2 } from "lucide-react";
import { fmtTime } from "@/lib/api";
import { DeckVisualizer, DECK_MODES } from "@/components/listen/DeckVisualizer";
import { faceplateClass } from "@/lib/theme";
import { EqPanel } from "@/components/listen/EqPanel";

const BeatLed = ({ clock }) => {
  const dot = useRef(null);
  useEffect(() => {
    let raf;
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (dot.current) dot.current.style.opacity = String(0.2 + 0.8 * clock.pulse(now).pulse);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [clock]);
  return <span ref={dot} className="inline-block w-2 h-2 rounded-full lcd-glow" style={{ background: "var(--t-label)" }} />;
};

// Rotary volume knob: drag up/down, scroll, or use arrow keys.
const Knob = ({ vol }) => {
  const start = useRef(null);
  const knob = useRef(null);
  const volRef = useRef(vol);
  volRef.current = vol;
  useEffect(() => {
    const el = knob.current;
    const onWheel = (e) => { e.preventDefault(); volRef.current.setVolume(volRef.current.volume - Math.sign(e.deltaY) * 4); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  const angle = -135 + (vol.volume / 100) * 270;
  const onDown = (e) => {
    start.current = { y: e.clientY, v: vol.volume };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => { if (start.current) vol.setVolume(start.current.v + (start.current.y - e.clientY) / 2); };
  return (
    <div className="flex flex-col items-center gap-1.5 shrink-0">
      <div role="slider" tabIndex={0} aria-label="Volume" aria-valuenow={vol.volume} aria-valuemin={0} aria-valuemax={100}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => { start.current = null; }}
        ref={knob}
        onKeyDown={(e) => {
          const step = { ArrowUp: 5, ArrowRight: 5, ArrowDown: -5, ArrowLeft: -5 }[e.key];
          if (step) { e.preventDefault(); vol.setVolume(vol.volume + step); }
        }}
        className="deck-knob w-16 h-16 sm:w-20 sm:h-20 rounded-full cursor-ns-resize touch-none relative" data-testid="deck-volume-knob">
        <span className="absolute left-1/2 top-1/2 w-1 h-[42%] -ml-0.5 origin-bottom rounded-full" style={{ transform: `translateY(-100%) rotate(${angle}deg)`, background: "var(--t-volume)", boxShadow: "0 0 6px var(--t-volume)" }} />
      </div>
      <span className="font-mono text-[0.6rem] tracking-[0.25em] deck-legend" data-testid="deck-volume-value">VOL {vol.volume}</span>
    </div>
  );
};

const DeckBtn = ({ children, onClick, active, testid, className = "", label }) => (
  <button onClick={onClick} aria-label={label} className={`deck-btn rounded-md h-9 px-2 font-mono text-[0.65rem] tracking-widest flex items-center justify-center gap-1.5 ${className}`} data-testid={testid}>
    {active !== undefined && <span className={`w-1.5 h-1.5 rounded-full ${active ? "lcd-glow" : ""}`} style={{ background: active ? "var(--t-label)" : "#3a3a3a" }} />}
    {children}
  </button>
);

export const CarDeck = ({ status, elapsed, audio, clock, vol }) => {
  const np = status?.now_playing;
  const pct = np?.duration ? Math.min(100, (elapsed / np.duration) * 100) : 0;
  const bpm = Math.round(np?.bpm_now || np?.bpm || 0);
  const [mode, setMode] = useState(() => localStorage.getItem("deck-mode") || "AUTO");
  const [autoShown, setAutoShown] = useState("SPEC");
  const [eqOpen, setEqOpen] = useState(false);
  useEffect(() => { localStorage.setItem("deck-mode", mode); }, [mode]);
  const cycle = () => setMode(DECK_MODES[(DECK_MODES.indexOf(mode) + 1) % DECK_MODES.length]);
  const shownMode = mode === "AUTO" ? autoShown : mode;

  return (
    <section className={`car-deck ${faceplateClass(status?.station?.theme?.deck_faceplate)} rounded-[18px] max-w-4xl mx-auto w-full p-3 sm:p-4 rise`}
      data-faceplate={status?.station?.theme?.deck_faceplate || "custom"} data-player="alpine" data-testid="public-player-card">
      <div className="flex items-center gap-3 mb-3 px-1">
        <span className="font-display text-sm sm:text-base tracking-[0.3em] deck-brand" data-testid="deck-brand">{(status?.station?.name || "Lowride Life").toUpperCase()}</span>
        <div className="deck-slot flex-1 h-2.5 rounded-full" aria-hidden />
        <span className="font-mono text-[0.55rem] tracking-[0.3em] deck-legend hidden sm:inline">DIGITAL TUNER · 128K</span>
        <span className={`w-2 h-2 rounded-full ${audio.playing ? "on-air-pulse" : ""}`} style={{ background: audio.playing ? "var(--onair)" : "#3a1a1a" }} />
      </div>

      <div className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] gap-3 sm:gap-4 items-center">
        <Knob vol={vol} />

        <div className="lcd deck-lcd rounded-lg p-3 min-w-0 col-span-1 sm:col-span-1">
          <div className="flex items-center justify-between text-[0.55rem] sm:text-[0.6rem] tracking-[0.1em] sm:tracking-[0.2em] lcd-text font-mono gap-2 whitespace-nowrap">
            <span className="flex items-center gap-2 whitespace-nowrap shrink-0" data-testid="station-onair-status">
              {status?.live
                ? <span className="px-1.5 rounded-sm on-air-pulse" style={{ background: "var(--onair)", color: "#fff" }} data-testid="public-player-live-badge">LIVE</span>
                : <span>{np ? "ON AIR" : "OFF AIR"}</span>}
              <span className="opacity-70 hidden sm:inline" data-testid="deck-mode-label">{mode === "AUTO" ? `AUTO·${shownMode}` : shownMode}</span>
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap shrink-0" data-testid="console-bpm"><BeatLed clock={clock} />{bpm ? `${bpm} BPM` : "--- BPM"}</span>
            <span className="whitespace-nowrap shrink-0" data-testid="station-listener-count"><b>{status?.listeners ?? 0}</b> LSTN</span>
          </div>
          <DeckVisualizer clock={clock} audioApi={audio} playing={audio.playing} mode={mode} onAutoMode={(m) => m !== autoShown && setAutoShown(m)} />
          <div className="min-w-0 mt-1" data-testid="public-player-now-playing-title">
            <p className="block truncate font-display text-xl sm:text-2xl candy-text leading-tight">{np?.title || (status ? "Station idle" : "Tuning in...")}</p>
          </div>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-[0.65rem] lcd-text opacity-70 truncate flex-1 font-mono" data-testid="public-player-now-playing-artist">{np?.artist || status?.station?.tagline}</p>
            <span className="font-mono text-[0.65rem] lcd-text opacity-80 shrink-0">{fmtTime(np ? elapsed : 0)} / {fmtTime(np?.duration)}</span>
          </div>
          <div className="h-1 rounded-full mt-1.5 overflow-hidden" style={{ background: "color-mix(in srgb, var(--t-label) 15%, transparent)" }} data-testid="console-tuner">
            <div className="h-full lr-progress transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="public-player-progress" />
          </div>
        </div>

        <div className="col-span-2 sm:col-span-1 flex sm:flex-col gap-2 sm:w-28">
          <button onClick={audio.toggle} disabled={audio.loading} className="hw-btn rounded-lg flex-1 h-14 sm:h-16 flex items-center justify-center"
            data-testid={audio.playing ? "console-pause-button" : "console-play-button"} aria-label={audio.playing ? "Pause" : "Play"}>
            {audio.loading ? <Loader2 className="w-7 h-7 animate-spin hw-icon" /> : audio.playing ? <Pause className="w-8 h-8 hw-icon" /> : <Play className="w-8 h-8 hw-icon" />}
          </button>
          <div className="flex gap-2 flex-1">
            <DeckBtn onClick={vol.toggleMute} testid="deck-mute-button" className="flex-1" label="Mute">
              {vol.volume ? <Volume2 className="w-3.5 h-3.5 hw-icon" /> : <VolumeX className="w-3.5 h-3.5 hw-icon" />}
            </DeckBtn>
            <DeckBtn onClick={cycle} testid="deck-mode-button" className="flex-1" label="Next visualizer">MODE</DeckBtn>
            <DeckBtn onClick={() => setEqOpen(!eqOpen)} active={eqOpen} testid="deck-eq-button" className="flex-1" label="Equalizer">EQ</DeckBtn>
          </div>
        </div>
      </div>

      {eqOpen && <EqPanel audio={audio} className="lcd deck-lcd rounded-lg p-3 mt-3 lcd-text" />}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3" data-testid="deck-presets">
        {DECK_MODES.map((m, i) => (
          <DeckBtn key={m} onClick={() => setMode(m)} active={mode === m} testid={`deck-preset-${m.toLowerCase()}`}>
            <span className="opacity-60">{i + 1}</span> <span className="truncate">{m}</span>
          </DeckBtn>
        ))}
      </div>
    </section>
  );
};
